import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../../lib/i18n.js';
import { Toast } from './Feedback.js';
/** Long enough to read a sentence twice, and paused while the pointer or focus is on it. */
export const TOAST_DURATION_MS = 6000;
/** More than this at once is a list nobody reads; the oldest goes first. */
const MAX_TOASTS = 3;
const ToastContext = createContext(null);
/**
 * The one place toasts are shown from, mounted once per application above the
 * router's outlet, so a toast raised just before a navigation is still on
 * screen after it. That is the whole point of a toast in Athena: the action's
 * own screen is gone, and this is where the confirmation lands instead.
 *
 * - **Announced, never focused.** Toasts render inside one polite live region
 *   that is always mounted — a region that appears with its text is often not
 *   read — and nothing here moves focus. The close button is in the tab order
 *   for whoever wants it, at the end of the page.
 * - **No pile of "Saved".** Showing the message already on screen restarts
 *   that toast's time instead of adding a second one, and at most three are
 *   visible at once.
 * - **Timed, but not rushed.** Six seconds, paused while the pointer is over a
 *   toast or focus is inside one (WCAG 2.2.1), and dismissible by hand.
 * - **No motion to reduce.** Toasts appear and disappear without animation.
 */
export function ToastProvider({ children }) {
    const t = useTranslate();
    const [toasts, setToasts] = useState([]);
    // The list as the callbacks see it, kept beside the state so that showing a
    // toast has no side effects inside a state updater (StrictMode runs those
    // twice).
    const current = useRef([]);
    const timers = useRef(new Map());
    const paused = useRef(false);
    const nextId = useRef(0);
    const commit = useCallback((next) => {
        current.current = next;
        setToasts(next);
    }, []);
    const dismiss = useCallback((id) => {
        clearTimeout(timers.current.get(id));
        timers.current.delete(id);
        commit(current.current.filter((entry) => entry.id !== id));
    }, [commit]);
    const schedule = useCallback((id) => {
        clearTimeout(timers.current.get(id));
        timers.current.delete(id);
        if (paused.current)
            return;
        timers.current.set(id, setTimeout(() => dismiss(id), TOAST_DURATION_MS));
    }, [dismiss]);
    const show = useCallback((message, { tone = 'success' } = {}) => {
        const same = current.current.find((entry) => entry.message === message && entry.tone === tone);
        if (same) {
            schedule(same.id);
            return;
        }
        nextId.current += 1;
        const entry = { id: nextId.current, message, tone };
        const next = [...current.current, entry];
        for (const dropped of next.slice(0, Math.max(0, next.length - MAX_TOASTS))) {
            clearTimeout(timers.current.get(dropped.id));
            timers.current.delete(dropped.id);
        }
        commit(next.slice(-MAX_TOASTS));
        schedule(entry.id);
    }, [commit, schedule]);
    function pause() {
        paused.current = true;
        for (const timer of timers.current.values())
            clearTimeout(timer);
        timers.current.clear();
    }
    function resume() {
        paused.current = false;
        for (const entry of current.current)
            schedule(entry.id);
    }
    useEffect(() => {
        const pending = timers.current;
        return () => {
            for (const timer of pending.values())
                clearTimeout(timer);
        };
    }, []);
    const api = useMemo(() => ({ show }), [show]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, createPortal(_jsx("div", { "aria-live": "polite", "aria-atomic": "false", onMouseEnter: pause, onMouseLeave: resume, onFocus: pause, onBlur: (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget))
                        resume();
                }, 
                // Above whatever is fixed to the bottom of the screen — the phone
                // navigation, or the runner's buttons — by its measured height
                // (`--app-inset-bottom`), and out of the way of the page: pointer
                // events only on the toasts themselves.
                className: "pointer-events-none fixed inset-x-4 bottom-[calc(var(--app-inset-bottom,0px)+1rem)] z-50 flex flex-col items-center gap-2 md:inset-x-auto md:right-6 md:bottom-[calc(var(--app-inset-bottom,0px)+1.5rem)] md:items-end", children: toasts.map((entry) => (_jsx(Toast, { tone: entry.tone, live: false, onDismiss: () => dismiss(entry.id), dismissLabel: t('ui.toast.dismiss'), className: "pointer-events-auto w-full max-w-md md:w-auto", children: entry.message }, entry.id))) }), document.body)] }));
}
/**
 * `toast.show(message)`, from anywhere under a `ToastProvider`.
 *
 * Throws outside one, rather than quietly dropping the confirmation: a toast
 * that silently never appears is exactly the kind of feedback gap this exists
 * to close.
 */
export function useToast() {
    const api = useContext(ToastContext);
    if (!api)
        throw new Error('useToast must be used inside a ToastProvider');
    return api;
}
//# sourceMappingURL=Toaster.js.map